/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable, inject} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable, firstValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';
import {
  AppScope,
  Filter,
  FilterEntity,
  FilterSelect,
} from '@gatewee/types/filter';
import {Client} from '@gatewee/types/client';
import {GenericCrudService} from './generic-crud.service';
import {HttpClient} from '@angular/common/http';
import {Recipient} from '@gatewee/types/recipient';
import {Domain} from '@gatewee/types/domain';
import {Select2Data} from 'ng-select2-component';

@Injectable()
export class FilterService {
  private clientsServiceDomain: GenericCrudService<Client>;
  private clientsServiceRecipient: GenericCrudService<Client>;
  private recipientsServiceDomain: GenericCrudService<Recipient>;
  private recipientsServiceAdmin: GenericCrudService<Recipient>;
  private domainsServiceAdmin: GenericCrudService<Domain>;
  private http: HttpClient = inject(HttpClient);
  private translate: TranslateService = inject(TranslateService);

  private readonly hierarchyMap: Record<AppScope, FilterEntity[]> = {
    admin: ['domain', 'recipient', 'client'],
    domain: ['recipient', 'client'],
    recipient: ['client'],
  };

  constructor() {
    this.clientsServiceDomain = new GenericCrudService<Client>(
      '/clients/domain',
      this.http,
    );
    this.clientsServiceRecipient = new GenericCrudService<Client>(
      '/clients/recipient',
      this.http,
    );
    this.recipientsServiceDomain = new GenericCrudService<Recipient>(
      '/recipients/domain',
      this.http,
    );
    this.recipientsServiceAdmin = new GenericCrudService<Recipient>(
      '/recipients/admin',
      this.http,
    );
    this.domainsServiceAdmin = new GenericCrudService<Domain>(
      '/domains/admin',
      this.http,
    );
  }

  // ========== DATA-FETCHING HELPERS ==========

  /** Fetch domain options for select filters (admin scope only). */
  async fetchDomainOptions(): Promise<Select2Data> {
    try {
      const data = await firstValueFrom(
        this.domainsServiceAdmin.findAll(
          {options: {select: {id: true, name: true}}},
          true,
        ),
      );
      return [
        {label: this.translate.instant('common.filters.all'), value: ''},
        ...data.map((d: Domain) => ({
          label: d.name ?? `#${d.id}`,
          value: d.id,
        })),
      ];
    } catch (error) {
      console.error('Error fetching domains:', error);
      return [{label: this.translate.instant('common.filters.all'), value: ''}];
    }
  }

  /** Fetch recipient options, optionally scoped to a domain. */
  async fetchRecipientOptions(
    scope: AppScope,
    domainId?: string | number,
  ): Promise<Select2Data> {
    try {
      const service =
        scope === 'admin'
          ? this.recipientsServiceAdmin
          : this.recipientsServiceDomain;

      const filters: Record<string, unknown> = {
        options: {select: {id: true, name: true}},
      };
      if (domainId) {
        filters['options'] = {
          ...(filters['options'] as object),
          where: {domain: {id: domainId}},
        };
      }

      const data = await firstValueFrom(service.findAll(filters, true));
      return [
        {label: this.translate.instant('common.filters.all'), value: ''},
        ...data.map((r: Recipient) => ({
          label: r.name ?? `#${r.id}`,
          value: r.id,
        })),
      ];
    } catch (error) {
      console.error('Error fetching recipients:', error);
      return [{label: this.translate.instant('common.filters.all'), value: ''}];
    }
  }

  /** Inner `options` object for client list / paginated queries. */
  private buildClientListOptions(
    recipientId?: string | number,
    domainId?: string | number,
  ): Record<string, unknown> {
    const where: Record<string, unknown> = {};
    if (recipientId) {
      where['recipient'] = {id: recipientId};
    }
    if (domainId) {
      where['domain'] = {id: domainId};
    }
    return {
      select: {id: true, name: true},
      ...(Object.keys(where).length > 0 ? {where} : {}),
    };
  }

  // ========== CASCADING ENTITY LOGIC ==========

  /**
   * Handles cascading logic when an entity filter changes.
   * - Parent selected: refetch and clear child entity filters
   * - Child selected: clear parent entity filters
   * - Entity cleared: reset child entity filters to unscoped options
   */
  async handleEntityCascade(
    scope: AppScope,
    changedKey: string,
    changedValue: unknown,
    currentFilters: Filter[],
  ): Promise<Filter[]> {
    const changedFilter = currentFilters.find(f => f.key === changedKey);
    if (!changedFilter?.entity) return [...currentFilters];

    const hierarchy = this.hierarchyMap[scope];
    const changedLevel = hierarchy.indexOf(changedFilter.entity);
    if (changedLevel === -1) return [...currentFilters];

    const filters = currentFilters.map(f => ({...f}));

    for (const f of filters) {
      if (!f.entity || f.key === changedKey) continue;
      const level = hierarchy.indexOf(f.entity);

      if (changedValue) {
        if (level > changedLevel) {
          f.value = '';
          f.selectedLabel = undefined;
        } else if (level < changedLevel) {
          f.value = '';
          f.selectedLabel = undefined;
        }
      } else if (level > changedLevel) {
        f.value = '';
        f.selectedLabel = undefined;
      }
    }

    return filters;
  }

  /**
   * CRUD source for entity filters (`gatewee-select-async`).
   * Domain recipients are listed via `GET /recipients/domain` only (no `/paginated`
   * in this app); we adapt `findAll` to the paginated contract expected by the select.
   */
  getEntityCrudSource(entity: FilterEntity, scope: AppScope) {
    const base = this.getEntityCrudService(entity, scope);
    if (entity === 'recipient' && scope === 'domain') {
      return this.asFindAllPaginatedSource(base);
    }
    return base;
  }

  private getEntityCrudService(
    entity: FilterEntity,
    scope: AppScope,
  ): GenericCrudService<Domain | Recipient | Client> {
    switch (entity) {
      case 'domain':
        return this.domainsServiceAdmin;
      case 'recipient':
        return scope === 'admin'
          ? this.recipientsServiceAdmin
          : this.recipientsServiceDomain;
      case 'client':
        return scope === 'recipient'
          ? this.clientsServiceRecipient
          : this.clientsServiceDomain;
    }
  }

  private asFindAllPaginatedSource<T>(service: GenericCrudService<T>): {
    findAllPaginated: (
      skip: number,
      take: number,
      filters?: Record<string, unknown>,
      skipSpinner?: boolean,
    ) => Observable<{records: T[]; count: number}>;
    findOne: (
      id: number,
      filters?: Record<string, unknown>,
      skipSpinner?: boolean,
    ) => Observable<T>;
  } {
    return {
      findAllPaginated: (skip, take, filters, skipSpinner) =>
        service.findAll(filters, skipSpinner).pipe(
          map(records => ({
            records: records.slice(skip, skip + take),
            count: records.length,
          })),
        ),
      findOne: (id, filters, skipSpinner) =>
        service.findOne(id, filters, skipSpinner),
    };
  }

  /** Gatewee `options` payload scoped by parent entity filters in the hierarchy. */
  buildEntityListOptions(
    target: FilterSelect,
    filters: Filter[],
    scope: AppScope,
  ): Record<string, unknown> {
    const hierarchy = this.hierarchyMap[scope];
    const constraints = this.buildEntityConstraintsFromFilters(
      filters,
      target.entity!,
      hierarchy,
    );

    if (target.entity === 'client') {
      return this.buildClientListOptions(
        constraints['recipient'] as string | number | undefined,
        constraints['domain'] as string | number | undefined,
      );
    }

    const where: Record<string, unknown> = {};
    if (constraints['domain']) {
      where['domain'] = {id: constraints['domain']};
    }
    if (constraints['recipient']) {
      where['recipient'] = {id: constraints['recipient']};
    }

    return {
      select: {id: true, name: true},
      ...(Object.keys(where).length > 0 ? {where} : {}),
    };
  }

  private buildEntityConstraintsFromFilters(
    filters: Filter[],
    targetEntity: FilterEntity,
    hierarchy: FilterEntity[],
  ): Record<string, unknown> {
    const targetLevel = hierarchy.indexOf(targetEntity);
    const constraints: Record<string, unknown> = {};

    for (const f of filters) {
      if (!f.entity || !f.value || f.value === '') {
        continue;
      }
      const level = hierarchy.indexOf(f.entity);
      if (level >= 0 && level < targetLevel) {
        constraints[f.entity] = f.value;
      }
    }

    return constraints;
  }

  // ========== PUBLIC UTILS ==========

  private static readonly PAGINATION_QUERY_KEYS = new Set([
    'page',
    'take',
    'skip',
  ]);

  /**
   * Main entry: parse incoming filters (from form/query params),
   * expand dot-paths, normalize types, and remove empty values.
   */
  parseFilters(
    filters: Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    if (!filters) return {};
    const parsed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(
      this.stripPaginationParams(filters),
    )) {
      parsed[key] = this.smartParse(value);
    }

    const expanded = this.expandDotPaths(parsed);

    const options = (expanded['options'] as Record<string, unknown>) ?? {};
    const cleanedWhere = this.compactObject(
      (options['where'] as Record<string, unknown>) ?? {},
    );
    if (Object.keys(cleanedWhere).length > 0) {
      options['where'] = cleanedWhere;
    } else {
      delete options['where'];
    }

    if (Object.keys(options).length > 0) {
      expanded['options'] = options;
    } else {
      delete expanded['options'];
    }

    return expanded;
  }

  /** Same parsing as list queries, but flattened for POST /reports/:scope (no `options` wrapper). */
  parseReportFilters(
    filters: Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    const parsed = this.parseFilters(filters);
    const options = parsed['options'];
    if (options && typeof options === 'object' && !Array.isArray(options)) {
      return options as Record<string, unknown>;
    }
    return parsed;
  }

  // ========== INTERNAL HELPERS ==========

  /** Remove URL pagination keys that must not be sent as API filter params. */
  private stripPaginationParams(
    params: Record<string, unknown>,
  ): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(params).filter(
        ([k]) => !FilterService.PAGINATION_QUERY_KEYS.has(k),
      ),
    );
  }

  private expandDotPaths(
    record: Record<string, unknown>,
  ): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [k, raw] of Object.entries(record)) {
      const v = this.smartParse(raw);
      if (k.includes('.')) {
        this.setByPath(out as any, k, v);
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  private smartParse(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (trimmed === '') return '';
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed === 'null') return null;
    if (!Number.isNaN(Number(trimmed))) return Number(trimmed);

    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return value;
      }
    }
    return value;
  }

  private setByPath<T extends Record<string, any>>(
    obj: T,
    path: string,
    value: any,
  ): T {
    const parts = path.split('.').filter(Boolean);
    let cur: any = obj;
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i]!;
      const isLast = i === parts.length - 1;
      if (isLast) {
        cur[p] = value;
      } else {
        if (typeof cur[p] !== 'object' || cur[p] === null) cur[p] = {};
        cur = cur[p];
      }
    }
    return obj;
  }

  /** Remove empty strings, null, undefined, and recursively prune empty objects */
  private compactObject<T extends Record<string, any>>(obj: T): T {
    const out: any = Array.isArray(obj) ? [] : {};
    for (const [k, v] of Object.entries(obj)) {
      if (v === '' || v === null || v === undefined) continue;

      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const nested = this.compactObject(v as Record<string, unknown>);
        if (Object.keys(nested).length > 0) out[k] = nested;
      } else {
        out[k] = v;
      }
    }
    return out;
  }
}
